<template>
    <div>
        <DotsLoading v-if="isLoading" />
        <template v-else>
            <RouteHeader
                v-if="route.noParcours"
                v-model="selectedDate"
                :route="route"
                :notices="notices"
                :general-notices="generalNotices"
                :is-printing-available="!!stops && stops.length > 0"
                :invalid-date="query.fd"
                class="route-header-wrapper"
            >
                <template #content>
                    <div :class="headerClass">
                        <VirtualStation
                            class="route-header"
                            :route="route"
                            :has-notices="notices.length !== 0"
                            :is-today="false"
                        >
                            <template #stop-content>
                                <div class="direction-wrapper">
                                    <h3 class="direction-title">{{ t('to_direction') }} {{ direction.description }}</h3>
                                    <RouterLink
                                        v-if="!unidirectionnal && reverseDirection"
                                        :to="{
                                            name: 'route-single',
                                            params: {
                                                route: params.route,
                                                direction: reverseDirection,
                                                date: selectedDate,
                                            },
                                        }"
                                        replace
                                        role="button"
                                        class="direction-link"
                                    >
                                        <div class="reverse-button-wrapper button">
                                            <IconReverse width="20" height="20" />
                                            {{ t('reverse_route') }}
                                        </div>
                                    </RouterLink>
                                </div>
                            </template>
                        </VirtualStation>
                    </div>
                </template>
            </RouteHeader>
            <div v-if="!stops || stops.length === 0" class="route-container no-departures-message">
                <div v-if="!stops">
                    <p>
                        {{ t('error_occured') }}
                    </p>
                    <p>{{ t('try_again_later') }}</p>
                </div>
                <div v-else>
                    <p>{{ t('no_departures_date') }}</p>
                    <p>{{ t('choose_another_date') }}</p>
                </div>
            </div>
            <template v-else>
                <div v-if="route" class="route-container">
                    <h2 class="visuallyhidden">{{ t('choose_stop') }}</h2>
                    <p class="route-subtitle">
                        <IconClock width="16" height="16" />
                        {{ t('choose_stop_schedule') }}
                    </p>
                    <TabList class="route-tabs" :enabled="!isDesktop" heading-level="h3" :tabs="routeTabs">
                        <template #tab0>
                            <h3 class="stop-title show-for-large">{{ t('list') }}</h3>
                            <StopList
                                class="stop-list"
                                :stops="stops"
                                :route="route"
                                :direction="direction"
                                :accessible="route.accessible"
                                :selected-date="selectedDate"
                            ></StopList>
                        </template>
                        <template #tab1>
                            <h3 class="map-title show-for-large">{{ t('map') }}</h3>
                            <RouteMap
                                class="map-container"
                                :route="route"
                                :direction="direction"
                                :selected-date="selectedDate"
                                :stops="stops"
                            ></RouteMap>
                        </template>
                    </TabList>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup>
import { useWindowSize, useDebounce } from '@vueuse/core';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import { addDays, isBefore, isAfter } from '@@/js/utils/dateFns';
import { constants } from '@@/js/utils/constants';
import { useSchedulesStore } from '@@/js/stores/schedules';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import { useBreadcrumb } from '@/schedules/composables/useBreadcrumb';
import directionSlug from '@@/js/utils/directionSlug';
import DotsLoading from '@/shared/DotsLoading';
import RouteHeader from '@/schedules/components/RouteHeader';
import RouteMap from '@/schedules/components/RouteMap';
import StopList from '@/schedules/components/StopList';
import TabList from '@/shared/TabList';
import VirtualStation from '@/schedules/components/VirtualStation';

import IconReverse from '@@/icons/reverse.svg';
import IconClock from '@@/icons/clock.svg';
import IconList from '@@/icons/list.svg';
import IconMapPin from '@@/icons/map-pin.svg';

const jsonld = ref(null);

const script = computed(() =>
    jsonld.value
        ? [
              {
                  type: 'application/ld+json',
                  children: JSON.stringify(jsonld.value),
              },
          ]
        : [],
);

const { locale, t } = useI18n({
    messages: {
        fr: {
            all_routes_title: 'Tous les parcours',
            to_direction: 'Vers {direction}',
            reverse_route: 'Voir l’autre direction',
            error_occured: "Une erreur s'est produite et nous sommes désolés de cet inconvénient.",
            try_again_later: 'Veuillez réessayer plus tard.',
            no_departures_date: "Il n'y a aucun passage pour cette date.",
            choose_another_date: 'Veuillez choisir une autre date.',
            choose_stop: 'Choisir un arrêt',
            list: 'Dans la liste',
            map: 'Sur la carte',
            route_to: 'Parcours {number} vers {direction}',
            go_back_to_all_routes: 'Retour à tous les parcours',
            path_available_from: 'Tracé en vigueur à partir du : {date}',
            path_available_since: 'Tracé en vigueur depuis le : {date}',
            see_next_path: 'Voir le tracé en vigueur à partir du : {date}',
            see_actual_path: 'Voir le tracé en vigueur actuellement',
            choose_stop_schedule: 'Pour consulter les horaires, choisir un arrêt dans la liste ou sur la carte',
        },
        en: {
            all_routes_title: 'All routes',
            to_direction: 'To {direction}',
            reverse_route: 'See the other direction',
            error_occured: 'An error has occured and we apologize for any inconvenience.',
            try_again_later: 'Please try again later.',
            no_departures_date: 'There is no departures for this date.',
            choose_another_date: 'Please choose another date.',
            choose_stop: 'Choose a stop',
            list: 'In the list',
            map: 'On the map',
            route_to: 'Route {number} to {direction}',
            go_back_to_all_routes: 'Go back to all routes',
            path_available_from: 'Path available from : {date}',
            path_available_since: 'Path available since : {date}',
            see_next_path: 'See the applicable path starting from : {date}',
            see_actual_path: 'See the currently applicable path',
            choose_stop_schedule: 'To view schedules, choose a stop in the list or on the map',
        },
    },
});

const isLoading = ref(true);
const route = ref({});
const direction = ref({});

useHead({
    script,
    title: () => isLoading.value ? `${t('all_routes_title')} | RTC` : `${t('route_to', { number: route.value.noParcours, direction: direction.value.description })} | RTC`,
});

const { width } = useWindowSize();
const debouncedWidth = useDebounce(width, 100);
const isDesktop = computed(() => debouncedWidth.value > 1024);

const vRoute = useRoute();
const { params, name, query } = vRoute;

const router = useRouter();

const schedulesStore = useSchedulesStore();
const { routes, fetchRoute, getDirectionDetails, fetchStops, getRouteNotices, getServiceType } = schedulesStore;
const { generalNotices } = storeToRefs(schedulesStore);

const { getEffectiveDate } = useEffectiveDate();
const selectedDate = ref(params.date || getEffectiveDate(new Date(), 'yyyy-MM-dd'));

watch(selectedDate, (newDate) => {
    router.replace({ name, params: { ...params, date: newDate } });
});

const service = computed(() => getServiceType(route.value.codeTypeService));
const headerClass = computed(() => {
    return service.value.class || '';
});
const routeTabs = [
    { name: t('list'), icon: IconList },
    { name: t('map'), icon: IconMapPin },
];

const reverseDirection = computed(() => (
    direction.value.code === route.value.codeDirectionPrincipale
        ? directionSlug(route.value.codeDirectionRetour, route.value.descriptionDirectionRetour)
        : directionSlug(route.value.codeDirectionPrincipale, route.value.descriptionDirectionPrincipale)
));
const unidirectionnal = computed(() => {
    return !route.value.descriptionDirectionPrincipale || !route.value.descriptionDirectionRetour;
});

const stops = ref([]);
// Avis chargés une seule fois par le store, sans bloquer la page : ils s'affichent dès qu'ils arrivent
const notices = computed(() => getRouteNotices(params.route, direction.value.code));

const { addBreadcrumbLinks } = useBreadcrumb();

onMounted(async () => {
    try {
        const range = constants.CALENDAR_RANGE;
        const today = getEffectiveDate(new Date(), 'yyyy-MM-dd');
        const lastAvailableDate = getEffectiveDate(addDays(new Date(), range), 'yyyy-MM-dd');
        const date = params.date || today;

        if (isBefore(date, today)) {
            router.replace({ name, params: { ...params, date: today } });
            return;
        }
        if (isAfter(date, lastAvailableDate)) {
            router.replace({ name, params: { ...params, date: today }, query: { fd: date } });
            return;
        }

        route.value = await fetchRoute(params.route, date);
        if (!route.value) {
            // Si la route n'existe pas pour la période actuelle, on vérifie si elle existe en général
            // pour obtenir ses informations de base
            const routeInfo = routes.find((r) => r.noParcours === params.route);
            if (!routeInfo) {
                router.replace({ name: 'route-list' });
                return;
            }
            route.value = routeInfo;
        }

        if (!params.direction) {
            const defaultDirection = route.value.descriptionDirectionPrincipale
                ? directionSlug(route.value.codeDirectionPrincipale, route.value.descriptionDirectionPrincipale)
                : directionSlug(route.value.codeDirectionRetour, route.value.descriptionDirectionRetour);
            router.replace({ name, params: { ...params, direction: defaultDirection } });
            return;
        }

        direction.value = getDirectionDetails(route.value, params.direction);

        stops.value = (await fetchStops(params.route, direction.value.code, date)) || [];

        if (stops.value.length > 1) {
            // SEO schema
            jsonld.value = {
                '@context': 'http://schema.org/',
                '@type': 'BusTrip',
                busName: direction.value.description,
                busNumber: route.value.noParcours,
                departureBusStop: {
                    '@type': 'BusStop',
                    name: `${stops.value[0].noArret} - ${stops.value[0].nom}`,
                },
                arrivalBusStop: {
                    '@type': 'BusStop',
                    name: `${stops.value[stops.value.length - 1].noArret} - ${stops.value[stops.value.length - 1].nom}`,
                },
            };
        }

        const pageTitle = document.querySelector('h1');
        if (pageTitle) {
            pageTitle.innerText = `${service.value.name} ${route.value.noParcours} - ${t('to_direction', { direction: direction.value.description })}`;
        }

        // Liens à ajouter au fil d'Ariane
        const links = [
            {
                'text': `${service.value.name} ${route.value.noParcours}`,
                'url': locale.value === 'fr'
                    ? `/horaires-et-trajets/horaires-des-bus/tous-les-parcours/${route.value.noParcours}`
                    : `/en/schedules-and-routes/bus-schedule/all-routes/${route.value.noParcours}`,
            },
            {
                'text': direction.value.description,
                'url': 'current-page',
            },
        ];
        addBreadcrumbLinks(links);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        stops.value = null;
    }
    isLoading.value = false;
});
</script>

<style lang="scss" scoped>
@include colorize {
    .reverse-button-wrapper {
        color: $color-white;
        background-color: get-color-variation('hover');
        border: 0;
        &:hover,
        &:focus {
            background-color: get-color-variation('semiDark');
        }
        svg {
            fill: currentColor;
        }
    }
}
.route-header {
    margin-bottom: 16px;
}
.back-link {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    svg {
        transform: rotate(180deg);
    }
}
@include colorize {
    .route-header {
        background-color: get-color-variation('base');
    }
}
.direction-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.direction-title {
    margin-right: 8px;
    margin-bottom: 0;
}
.direction-link {
    font-size: 0;
}
.route-container {
    margin: 0 -16px;
    background-color: $color-black-0;
    border-radius: 0 0 20px 20px;
    @include breakpoint(large) {
        margin: 0;
    }
}
.no-departures-message {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    font-weight: 300;
    border-radius: 0 0 20px 20px;
    p {
        margin: 0;
        text-align: center;
    }
    @include breakpoint(medium) {
        @include rem-typo(20px);
        height: 385px;
    }
}
.route-subtitle {
    @include rem-typo(16px);
    display: flex;
    margin: 0;
    padding: 0 16px 16px;
    font-weight: 600;
    gap: 8px;
    svg {
        fill: $color-blue-75;
        flex-shrink: 0;
    }
    @include breakpoint(medium) {
        @include rem-typo(18px);
    }
}
.route-tabs {
    position: relative;
    background-color: $color-white;
    padding: 16px;
    border-radius: 15px;
    @include breakpoint(large) {
        display: grid;
        grid-template-columns: 40% calc(60% - 32px);
        gap: 16px;
        padding: 0;
    }
    @include breakpoint(xlarge) {
        gap: 32px;
    }
}
.stop-list {
    @include breakpoint(medium) {
        padding-left: 16px;
        padding-bottom: 16px;
    }
}
.stop-title {
    @include rem-typo(20px);
    margin: 0;
    padding: 16px;
}
.map-title {
    @include rem-typo(20px);
    margin: 0;
    padding: 16px 16px 16px 0;
}
.map-container {
    width: 100%;
    height: 450px;
    @include breakpoint(large) {
        height: 100vh;
        position: sticky;
        top: 0;
    }
}
</style>
